import React, { useMemo, useState, } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import apiClient from '../config/axios';
import toast from 'react-hot-toast';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { formatIndiaTime } from '../config/india-timezone';
import { FileText, Plus, Search, Edit, Trash2, Copy, X, Eye, Download } from 'lucide-react';

const Templates = () => {
  const { currentWorkspace } = useWorkspace();
  const queryClient = useQueryClient();

  // Debug: Log every render

  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('software'); // 'software' or 'facebook'
  const [templateFilter, setTemplateFilter] = useState('30'); // Filter by days: '7', '30', '90', '365', 'all'
  const [showCreateModal, setShowCreateModal] = useState(false);
  // const [showBulkCreateModal, setShowBulkCreateModal] = useState(false);
  const [newTemplate, setNewTemplate] = useState({
    name: '',
    content: '',
    language: 'en_US',
    parameters: []
  });
  const [creating, setCreating] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [editingTemplateId, setEditingTemplateId] = useState(null);

  // Fetch templates using React Query
  const { data: templatesData } = useQuery(
    ['templates', currentWorkspace?.id, searchTerm],
    async () => {
      if (!currentWorkspace?.id) {
        return { templates: [] };
      }

      const headers = { 'x-workspace-id': currentWorkspace.id };
      const response = await apiClient.get('/api/templates', {
        params: { page: 1, limit: 50, search: searchTerm || undefined },
        headers
      });
      return response.data;
    },
    {
      enabled: !!currentWorkspace?.id,
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Failed to fetch templates');
      }
    }
  );

  // Sync Facebook templates mutation
  const syncTemplatesMutation = useMutation(
    async () => {
      const headers = { 'x-workspace-id': currentWorkspace.id };
      const response = await apiClient.post('/api/whatsapp/templates/sync', {}, { headers });
      return response.data;
    },
    {
      onSuccess: (data) => {
        toast.success(data.message || `Synced ${data.saved} templates successfully`);
        queryClient.invalidateQueries(['facebookTemplates', currentWorkspace?.id]);
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Failed to sync templates');
      }
    }
  );

  // Fetch Facebook templates using React Query
  const { data: facebookTemplatesData, isLoading: facebookLoading } = useQuery(
    ['facebookTemplates', currentWorkspace?.id, templateFilter],
    async () => {
      if (!currentWorkspace?.id) {
        return { templates: [] };
      }

      const headers = { 'x-workspace-id': currentWorkspace.id };
      try {
        const response = await apiClient.get(`/api/whatsapp/templates?days=${templateFilter}`, { headers });
        return response.data;
      } catch (error) {
        console.warn('Facebook templates fetch failed:', error.response?.data);
        return { templates: [] };
      }
    },
    {
      enabled: !!currentWorkspace?.id,
      onSuccess: (data) => {
        // Auto-sync if no cached templates found
        if (data.templates.length === 0 && !data.cached && !syncTemplatesMutation.isLoading) {
          console.log('📊 No cached templates found, triggering auto-sync...');
          syncTemplatesMutation.mutate();
        }
      }
    }
  );

  const templates = templatesData?.templates || [];
  const facebookTemplates = facebookTemplatesData?.templates || [];

  const filteredTemplates = useMemo(() => {
    return templates.filter((template) => {
      const hayName = (template.name || '').toLowerCase();
      const hayContent = (template.content || '').toLowerCase();
      const needle = searchTerm.toLowerCase();
      return hayName.includes(needle) || hayContent.includes(needle);
    });
  }, [templates, searchTerm]);

  const filteredFacebookTemplates = useMemo(() => {
    return facebookTemplates.filter((template) => {
      const hayName = (template.name || '').toLowerCase();
      const hayContent = (template.content || '').toLowerCase();
      const needle = searchTerm.toLowerCase();
      return hayName.includes(needle) || hayContent.includes(needle);
    });
  }, [facebookTemplates, searchTerm]);


  const handleNewTemplate = () => {
    setShowCreateModal(true);
  };

  const handleEditTemplate = (id) => {
    const template = templates.find((t) => t.id === id);
    if (!template) {
      toast.error('Template not found');
      return;
    }
    setEditingTemplateId(id);
    setNewTemplate({
      name: template.name,
      content: template.content,
      language: template.language || 'en_US',
      parameters: template.parameters || []
    });
    setShowCreateModal(true);
  };

  const handleCreateTemplate = async () => {
    try {
      if (!newTemplate.name.trim() || !newTemplate.content.trim()) {
        toast.error('Please fill in template name and content');
        return;
      }

      setCreating(true);

      // Ensure all fields are properly defined
      const templateData = {
        name: newTemplate.name.trim(),
        content: newTemplate.content.trim(),
        language: newTemplate.language || 'en_US',
        parameters: newTemplate.parameters || []
      };

      console.log('Saving template:', templateData);

      const headers = currentWorkspace?.id ? { 'x-workspace-id': currentWorkspace.id } : {};

      let response;
      if (editingTemplateId) {
        // Update existing template
        response = await apiClient.put(`/api/templates/${editingTemplateId}`, templateData, { headers });
        console.log('Template updated successfully:', response.data);
        toast.success('Template updated successfully');
      } else {
        // Create new template
        response = await apiClient.post('/api/templates', templateData, { headers });
        console.log('Template created successfully:', response.data);
        toast.success('Template created successfully');
      }

      setShowCreateModal(false);
      setEditingTemplateId(null);
      setNewTemplate({ name: '', content: '', language: 'en_US', parameters: [] });
      queryClient.invalidateQueries(['templates', currentWorkspace?.id]);
    } catch (error) {
      console.error('Save template error:', error);
      const errorMessage = error.response?.data?.error || error.response?.data?.message || 'Failed to save template';
      toast.error(errorMessage);
    } finally {
      setCreating(false);
    }
  };



  const handleCopyTemplate = async (id) => {
    try {
      const headers = currentWorkspace?.id ? { 'x-workspace-id': currentWorkspace.id } : {};
      await apiClient.post(`/api/templates/${id}/duplicate`, {}, { headers });
      toast.success('Template duplicated');
      queryClient.invalidateQueries(['templates', currentWorkspace?.id]);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to duplicate');
    }
  };


  const handleDeleteTemplate = async (id) => {
    try {
      const headers = currentWorkspace?.id ? { 'x-workspace-id': currentWorkspace.id } : {};
      await apiClient.delete(`/api/templates/${id}`, { headers });
      toast.success('Template deleted');
      queryClient.invalidateQueries(['templates', currentWorkspace?.id]);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to delete');
    }
  };


  const handlePreviewTemplate = (template) => {
    setPreviewTemplate(template);
    setShowPreviewModal(true);
  };



  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Templates</h1>
        <div className="flex items-center gap-2">
          {activeTab === 'software' && (
            <button
              onClick={handleNewTemplate}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center space-x-2"
            >
              <Plus className="h-4 w-4" />
              <span>New Template</span>
            </button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="mb-6">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('software')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${activeTab === 'software'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
            >
              <div className="flex items-center space-x-2">
                <FileText className="h-4 w-4" />
                <span>Software Templates</span>
                <span className="bg-gray-100 text-gray-600 px-2 py-1 rounded-full text-xs">
                  {templates.length}
                </span>
              </div>
            </button>
            <button
              onClick={() => setActiveTab('facebook')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${activeTab === 'facebook'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
            >
              <div className="flex items-center space-x-2">
                <FileText className="h-4 w-4" />
                <span>Facebook Templates</span>
                <span className="bg-gray-100 text-gray-600 px-2 py-1 rounded-full text-xs">
                  {facebookTemplates.length}
                </span>
              </div>
            </button>
          </nav>
        </div>
      </div>

      <div className="mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search templates..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        {activeTab === 'facebook' && (
          <div className="mt-4 flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <label className="text-sm font-medium text-gray-700">Show templates from:</label>
              <select
                value={templateFilter}
                onChange={(e) => setTemplateFilter(e.target.value)}
                className="px-3 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="7">Last 7 days</option>
                <option value="30">Last 30 days</option>
                <option value="90">Last 90 days</option>
                <option value="365">Last year</option>
                <option value="all">All time</option>
              </select>
              <span className="text-xs text-gray-500">
                Showing {facebookTemplates.length} templates
                {facebookTemplatesData?.cached && facebookTemplatesData?.lastSync && (
                  <span className="ml-2 text-green-600">
                    • Synced {formatIndiaTime(facebookTemplatesData.lastSync, 'relative')}
                  </span>
                )}
              </span>
            </div>
            <div className="flex items-center gap-3">
              {facebookTemplates.length === 0 && !facebookLoading && (
                <span className="text-xs text-yellow-600 bg-yellow-50 px-3 py-1 rounded">
                  No templates cached - Click sync to load
                </span>
              )}
              <button
                onClick={() => syncTemplatesMutation.mutate()}
                disabled={syncTemplatesMutation.isLoading}
                className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-4 py-2 rounded-lg hover:from-blue-700 hover:to-indigo-700 flex items-center gap-2 transition-all shadow-md hover:shadow-lg disabled:opacity-50"
              >
                <Download className={`h-4 w-4 ${syncTemplatesMutation.isLoading ? 'animate-spin' : ''}`} />
                <span>{syncTemplatesMutation.isLoading ? 'Syncing...' : 'Sync from Facebook'}</span>
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="grid gap-4">
        {(activeTab === 'software' ? filteredTemplates : filteredFacebookTemplates).map((template) => (
          <div key={template.id} className="bg-white border border-gray-200 rounded-xl p-6 shadow-lg hover:shadow-xl transition-all duration-200">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center space-x-2 mb-2">
                  {activeTab === 'software' ? (
                    <FileText className="h-5 w-5 text-blue-600" />
                  ) : (
                    <FileText className="h-5 w-5 text-green-600" />
                  )}
                  <h3 className="text-lg font-medium text-gray-900">{template.name}</h3>
                  {activeTab === 'software' && (
                    <span className={`px-2 py-1 text-xs rounded-full ${template.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                      {template.is_active ? 'Active' : 'Inactive'}
                    </span>
                  )}
                  {activeTab === 'facebook' && (
                    <span className={`px-2 py-1 text-xs rounded-full ${template.status === 'APPROVED' ? 'bg-green-100 text-green-800' :
                      template.status === 'PENDING' ? 'bg-yellow-100 text-yellow-800' :
                        template.status === 'REJECTED' ? 'bg-red-100 text-red-800' :
                          'bg-gray-100 text-gray-800'
                      }`}>
                      {template.status || 'Unknown'}
                    </span>
                  )}
                </div>
                <p className="text-sm text-gray-600 mb-2">
                  {activeTab === 'software' ? template.content :
                    (template.components && template.components.find(c => c.type === 'BODY')?.text) || 'No content available'}
                </p>
                <div className="flex items-center space-x-4 text-xs text-gray-500">
                  <span>Language: {template.language || 'en_US'}</span>
                  {activeTab === 'software' && (
                    <span>Created: {formatIndiaTime(template.created_at, 'relative')}</span>
                  )}
                  {activeTab === 'facebook' && (
                    <span>Created: {template.created_time ? formatIndiaTime(template.created_time, 'relative') : 'Unknown'}</span>
                  )}
                </div>
              </div>
              <div className="flex items-center space-x-2">
                {activeTab === 'software' ? (
                  <>
                    <button
                      onClick={() => handlePreviewTemplate(template)}
                      className="p-2 text-gray-400 hover:text-purple-600"
                      title="Preview Template"
                    >
                      <Eye className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleEditTemplate(template.id)}
                      className="p-2 text-gray-400 hover:text-blue-600"
                      title="Edit Template"
                    >
                      <Edit className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleCopyTemplate(template.id)}
                      className="p-2 text-gray-400 hover:text-green-600"
                      title="Duplicate Template"
                    >
                      <Copy className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteTemplate(template.id)}
                      className="p-2 text-gray-400 hover:text-red-600"
                      title="Delete Template"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </>
                ) : (
                  <div className="text-sm text-gray-500">
                    Facebook templates are read-only
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>


      {/* Create Template Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white w-full max-w-2xl rounded-xl shadow-xl">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold">
                {editingTemplateId ? 'Edit Template' : 'Create New Template'}
              </h3>
              <button onClick={() => {
                setShowCreateModal(false);
                setEditingTemplateId(null);
                setNewTemplate({ name: '', content: '', language: 'en_US', parameters: [] });
              }} className="p-2 text-gray-400 hover:text-gray-600">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <span className="flex items-center space-x-1">
                    <span>Template Name</span>
                    <span className="text-xs text-gray-500">(Required)</span>
                  </span>
                </label>
                <input
                  type="text"
                  value={newTemplate.name}
                  onChange={(e) => setNewTemplate({ ...newTemplate, name: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., welcome_message, order_confirmation, appointment_reminder"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Choose a unique name to identify this template (use lowercase letters and underscores)
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <span className="flex items-center space-x-1">
                    <span>Language</span>
                    <span className="text-xs text-gray-500">(Optional)</span>
                  </span>
                </label>
                <select
                  value={newTemplate.language}
                  onChange={(e) => setNewTemplate({ ...newTemplate, language: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="en_US">🇺🇸 English (US)</option>
                  <option value="hi_IN">🇮🇳 Hindi (India)</option>
                  <option value="gu_IN">🇮🇳 Gujarati (India)</option>
                  <option value="ta_IN">🇮🇳 Tamil (India)</option>
                  <option value="te_IN">🇮🇳 Telugu (India)</option>
                </select>
                <p className="mt-1 text-xs text-gray-500">Choose the language for your template</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <span className="flex items-center space-x-1">
                    <span>Message Content</span>
                    <span className="text-xs text-gray-500">(Required)</span>
                  </span>
                </label>

                {/* Example Templates */}
                <div className="mb-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <h4 className="text-sm font-medium text-blue-900 mb-2">💡 Quick Examples:</h4>
                  <div className="space-y-2 text-xs">
                    <div className="flex items-start space-x-2">
                      <span className="text-blue-600">📝</span>
                      <div>
                        <span className="font-medium">Welcome:</span> "Hello {'{{1}}'}, welcome to {'{{2}}'}! How can we help you today?"
                      </div>
                    </div>
                    <div className="flex items-start space-x-2">
                      <span className="text-blue-600">📦</span>
                      <div>
                        <span className="font-medium">Order:</span> "Hi {'{{1}}'}, your order #{'{{2}}'} for ₹{'{{3}}'} has been confirmed!"
                      </div>
                    </div>
                    <div className="flex items-start space-x-2">
                      <span className="text-blue-600">📅</span>
                      <div>
                        <span className="font-medium">Appointment:</span> "Reminder: Your appointment with {'{{1}}'} is on {'{{2}}'} at {'{{3}}'}."
                      </div>
                    </div>
                  </div>
                </div>

                <textarea
                  rows={4}
                  value={newTemplate.content}
                  onChange={(e) => setNewTemplate({ ...newTemplate, content: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Type your message here...\nExample: Hello {{1}}, your order {{2}} is ready!"
                />

                <div className="mt-2 p-3 bg-gray-50 border rounded-lg">
                  <h4 className="text-sm font-medium text-gray-700 mb-1">🔧 How to use variables:</h4>
                  <div className="text-xs text-gray-600 space-y-1">
                    <p>• Use <code className="bg-gray-200 px-1 rounded">{'{{1}}'}</code> for the first variable (like customer name)</p>
                    <p>• Use <code className="bg-gray-200 px-1 rounded">{'{{2}}'}</code> for the second variable (like order number)</p>
                    <p>• Use <code className="bg-gray-200 px-1 rounded">{'{{3}}'}</code> for the third variable (like amount)</p>
                    <p>• When sending, you'll provide the actual values for these placeholders</p>
                  </div>
                </div>
              </div>
              <div className="flex items-center justify-end gap-2 pt-2">
                <button
                  onClick={() => {
                    setShowCreateModal(false);
                    setEditingTemplateId(null);
                    setNewTemplate({ name: '', content: '', language: 'en_US', parameters: [] });
                  }}
                  className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateTemplate}
                  disabled={creating}
                  className="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
                >
                  {creating ? (editingTemplateId ? 'Updating...' : 'Creating...') : (editingTemplateId ? 'Update Template' : 'Create Template')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Preview Template Modal */}
      {showPreviewModal && previewTemplate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white w-full max-w-4xl rounded-xl shadow-xl">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold">Preview Template: {previewTemplate.name}</h3>
              <button onClick={() => setShowPreviewModal(false)} className="p-2 text-gray-400 hover:text-gray-600">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* Template Info */}
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Template Details</label>
                    <div className="bg-gray-50 p-3 rounded-lg space-y-2">
                      <div className="flex items-center space-x-2">
                        <span className="text-sm font-medium">Name:</span>
                        <span className="text-sm text-gray-600">{previewTemplate.name}</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <span className="text-sm font-medium">Language:</span>
                        <span className="text-sm text-gray-600">{previewTemplate.language || 'en_US'}</span>
                      </div>
                    </div>
                  </div>

                </div>

                {/* Preview Content */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Template Content</label>
                  <div className="border rounded-lg p-4 min-h-[200px] bg-gray-50">
                    <div className="whitespace-pre-wrap text-sm text-gray-800">
                      {previewTemplate.content}
                    </div>
                  </div>
                </div>
              </div>


              <div className="flex items-center justify-end gap-2 pt-2">
                <button
                  onClick={() => setShowPreviewModal(false)}
                  className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default Templates;
